import type { ReactNode } from 'react';

import type { DocumentState, DocumentType, Verification } from '../../api/document';
import type { Translate } from '../../i18n';
import { useSession } from '../../session/useSession';
import { formatDate } from '../format';
import { Icon } from '../Icon';

export const TYPES: readonly DocumentType[] = ['transcript', 'minutes', 'certificate'];

/** D78 : tous les types, l'attestation de scolarité comprise (registre, tableau de bord). */
export const ALL_TYPES: readonly DocumentType[] = ['transcript', 'certificate', 'enrolment', 'minutes', 'card', 'list', 'internship'];

const STATE_TONES: Readonly<Record<DocumentState, string>> = {
    valid: 'ok',
    unsigned: 'violet',
    superseded: 'warn',
    cancelled: 'danger',
    expired: 'muted',
};

/** L'état d'un document produit (D78) : valable, remplacé, annulé, expiré. */
export function StateBadge({ state }: { readonly state: DocumentState }): ReactNode {
    const { t } = useSession();

    return <span className={`badge badge--${STATE_TONES[state]}`}>{t(`doc.docState.${state}`)}</span>;
}

export function typeLabel(t: Translate, type: DocumentType, plural = false): string {
    return t(plural ? `doc.types.${type}` : `doc.type.${type}`);
}

/** Le verdict d'une vérification, tel que l'écran Documents et la page publique le disent. */
export function VerificationNote({ result }: { readonly result: Verification }): ReactNode {
    const { t, locale } = useSession();
    const tone = result.state === 'valid' ? 'ok' : result.state === 'superseded' || result.state === 'expired' || result.state === 'unsigned' ? 'warn' : 'danger';
    const icon = result.state === 'valid' ? 'check' : result.state === 'cancelled' ? 'ban' : 'alert';

    return (
        <div className={`doc-verdict doc-verdict--${tone}`} role="status">
            <Icon name={icon} size={18} />
            <div>
                <strong>{t(`doc.verify.${result.state}`)}</strong>
                {result.state === 'unknown' ? (
                    <span>
                        {t('doc.verify.unknownBody', {
                            reference: result.reference,
                        })}
                    </span>
                ) : (
                    <span>
                        {t(result.type === 'enrolment' ? 'doc.verify.bodyEnrolment' : result.student === null || result.student === undefined ? 'doc.verify.bodyMinutes' : 'doc.verify.body', {
                            type: result.type === undefined ? '' : typeLabel(t, result.type),
                            student: result.student ?? '',
                            training: result.training ?? '',
                            year: result.year ?? '',
                            institution: result.institution ?? '',
                            date: result.issuedAt === undefined ? '' : (formatDate(result.issuedAt, locale) ?? ''),
                            by: result.issuedBy ?? '',
                            run: result.run ?? '',
                        })}
                        {result.state === 'superseded' && result.supersededAt !== null && result.supersededAt !== undefined ? ` ${t('doc.verify.supersededBody', { date: formatDate(result.supersededAt, locale) ?? '' })}` : null}
                        {result.state === 'altered' ? ` ${t('doc.verify.alteredBody')}` : null}
                        {result.state === 'cancelled' ? ` ${t('doc.verify.cancelledBody', { date: formatDate(result.cancelledAt ?? null, locale) ?? '' })}` : null}
                        {result.state === 'expired' ? ` ${t('doc.verify.expiredBody', { date: formatDate(result.validUntil ?? null, locale) ?? '' })}` : null}
                        {result.state === 'unsigned' ? ` ${t('doc.verify.unsignedBody')}` : null}
                        {result.signedAt !== null && result.signedAt !== undefined ? ` ${t('doc.verify.signed', { date: formatDate(result.signedAt, locale) ?? '', name: result.signedBy ?? '' })}` : null}
                        {result.state === 'valid' && result.validUntil !== null && result.validUntil !== undefined ? ` ${t('doc.verify.validUntil', { date: formatDate(result.validUntil, locale) ?? '' })}` : null}
                    </span>
                )}
            </div>
        </div>
    );
}
